import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { API_BASE, parseJsonResponse, getUserErrorMessage } from '../../utils/apiBase';
import { authFetch } from '../../utils/apiAuth';
import SuperPanelShell from '../../components/super/SuperPanelShell';
import { isUrduDominant } from '../../utils/noticeText';
import './superPanel.css';

export default function AnnouncementHeadings() {
  const [headings, setHeadings] = useState(['']);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const saveTimerRef = useRef(null);

  const fetchHeadings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/v1/settings/announcements`);
      const result = await parseJsonResponse(response);
      const lines = Array.isArray(result.data) ? result.data.filter(Boolean) : [];
      setHeadings(lines.length ? lines : ['']);
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Unable to load announcement headings.'));
      setHeadings(['']);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHeadings();
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [fetchHeadings]);

  const persistHeadings = useCallback(async (cleaned, { successMessage = 'Headings saved' } = {}) => {
    if (cleaned.length === 0) {
      toast.error('At least one heading is required');
      return false;
    }

    setSaving(true);
    try {
      const response = await authFetch(`${API_BASE}/v1/settings/announcements`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ headings: cleaned }),
      });
      const result = await parseJsonResponse(response);
      setHeadings(result.data || cleaned);
      toast.success(successMessage);
      return true;
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Unable to save headings.'));
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  const scheduleSave = (nextHeadings) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const cleaned = nextHeadings.map((line) => line.trim()).filter(Boolean);
      if (cleaned.length) persistHeadings(cleaned);
    }, 900);
  };

  const updateLine = (index, value) => {
    setHeadings((prev) => {
      const next = prev.map((line, i) => (i === index ? value : line));
      scheduleSave(next);
      return next;
    });
  };

  const addLine = () => setHeadings((prev) => [...prev, '']);

  const removeLine = async (index) => {
    const next = headings.filter((_, i) => i !== index);
    const cleaned = next.map((line) => line.trim()).filter(Boolean);
    if (cleaned.length === 0) {
      toast.error('At least one heading is required');
      return;
    }
    const saved = await persistHeadings(cleaned, { successMessage: 'Heading deleted and saved' });
    if (!saved) await fetchHeadings();
  };

  return (
    <SuperPanelShell
      title="Top Bar Headings"
      breadcrumb="Super Panel • Scrolling text bar"
      subtitle="Urdu/English lines in the thin bar above the menu (not the homepage image slider)"
    >
      <Toaster position="top-center" />

      <div className="sp-card" style={{ maxWidth: 820 }}>
        <div className="sp-card-body" style={{ padding: 20 }}>
          {loading ? (
            <p style={{ color: 'var(--sp-muted)', padding: 12 }}>Loading headings…</p>
          ) : (
            <div className="sp-heading-list">
              {headings.map((line, index) => (
                <div key={index} className="sp-heading-row">
                  <span className="sp-heading-num">{index + 1}</span>
                  <textarea
                    className="sp-form-input sp-heading-input"
                    value={line}
                    onChange={(e) => updateLine(index, e.target.value)}
                    placeholder="Urdu or English heading…"
                    dir={isUrduDominant(line) ? 'rtl' : 'ltr'}
                    rows={2}
                    disabled={saving}
                  />
                  <button
                    type="button"
                    className="sp-btn sp-btn--danger sp-btn--icon"
                    onClick={() => removeLine(index)}
                    disabled={saving || loading}
                    title="Remove line"
                  >
                    <FaTrash size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="sp-form-actions" style={{ marginTop: 16 }}>
            <button type="button" className="sp-btn sp-btn--ghost" onClick={addLine} disabled={loading || saving}>
              <FaPlus style={{ marginRight: 6 }} />
              Add Line
            </button>
            {saving && <span style={{ color: 'var(--sp-muted)', fontSize: 13 }}>Saving…</span>}
          </div>
        </div>
      </div>
    </SuperPanelShell>
  );
}
